'use client'

import { useEffect, useRef } from 'react'
import { useTheme } from 'next-themes'

interface Blob {
	x: number
	y: number
	vx: number
	vy: number
	radius: number
	hue: number
}

const BLOB_COUNT = 5

export function HeroAmbient() {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const themeRef = useRef('light')
	const frameRef = useRef(0)
	const { resolvedTheme } = useTheme()

	useEffect(() => {
		themeRef.current = resolvedTheme || 'light'
	}, [resolvedTheme])

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		const blobs: Blob[] = []
		for (let i = 0; i < BLOB_COUNT; i++) {
			blobs.push({
				x: Math.random(),
				y: Math.random(),
				vx: (Math.random() - 0.5) * 0.003,
				vy: (Math.random() - 0.5) * 0.002,
				radius: 0.15 + Math.random() * 0.2,
				hue: 208 + Math.random() * 12, // #50a2ff range
			})
		}

		let w = 0
		let h = 0

		function resize() {
			const dpr = window.devicePixelRatio || 1
			w = canvas!.clientWidth
			h = canvas!.clientHeight
			canvas!.width = w * dpr
			canvas!.height = h * dpr
			ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
		}

		const ro = new ResizeObserver(resize)
		ro.observe(canvas)
		resize()

		let visible = true
		function onVis() {
			visible = !document.hidden
			if (visible) {
				lastTime = 0
				frameRef.current = requestAnimationFrame(draw)
			}
		}
		document.addEventListener('visibilitychange', onVis)

		let lastTime = 0
		function draw(time: number) {
			if (!visible) return
			const dt = lastTime ? Math.min((time - lastTime) / 1000, 0.1) : 0.016
			lastTime = time

			const dark = themeRef.current === 'dark'

			ctx!.clearRect(0, 0, w, h)

			for (const b of blobs) {
				b.x += b.vx * dt
				b.y += b.vy * dt

				// Soft bounce off edges
				if (b.x < -0.1 || b.x > 1.1) b.vx *= -1
				if (b.y < -0.1 || b.y > 1.1) b.vy *= -1

				const px = b.x * w
				const py = b.y * h
				const r = b.radius * Math.max(w, h)

				const grad = ctx!.createRadialGradient(px, py, 0, px, py, r)
				if (dark) {
					grad.addColorStop(0, `hsla(${b.hue},70%,60%,0.06)`)
					grad.addColorStop(0.5, `hsla(${b.hue},70%,50%,0.03)`)
					grad.addColorStop(1, `hsla(${b.hue},70%,40%,0)`)
				} else {
					grad.addColorStop(0, `hsla(${b.hue},80%,55%,0.08)`)
					grad.addColorStop(0.5, `hsla(${b.hue},80%,50%,0.04)`)
					grad.addColorStop(1, `hsla(${b.hue},80%,45%,0)`)
				}

				ctx!.fillStyle = grad
				ctx!.fillRect(px - r, py - r, r * 2, r * 2)
			}

			frameRef.current = requestAnimationFrame(draw)
		}

		frameRef.current = requestAnimationFrame(draw)

		return () => {
			cancelAnimationFrame(frameRef.current)
			ro.disconnect()
			document.removeEventListener('visibilitychange', onVis)
		}
	}, [])

	return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none z-0" aria-hidden="true" />
}
